// Payment Gateway Abstraction Layer Types

export interface PaymentData {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cardholderName: string;
  billingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  paymentMethod?: 'credit_card' | 'debit_card';
  saveCard?: boolean;
  // Optional fields for buyer verification (Square SCA/3DS)
  amount?: number;
  currency?: string;
  email?: string;
  phone?: string;
}

export interface PaymentToken {
  token: string;
  type: 'payment_method_id' | 'nonce' | 'source_token';
  last_four_digits: string;
  card_brand: string;
  gateway_provider: string;
  exp_month?: number;  // Card expiration month (1-12)
  exp_year?: number;   // Card expiration year (4-digit)
  verification_token?: string;  // Square SCA/3DS verification token
  adminAudit?: {
    verbalAuthConfirmed: boolean;
    verbalAuthTimestamp: string | null;
    adminEnteredCard: boolean;
  };
}

export interface PaymentResult {
  success: boolean;
  payment_id: string;
  transaction_id?: string;
  status: 'succeeded' | 'failed' | 'requires_action' | 'requires_payment_method' | 'canceled';
  error_code?: string;
  error_message?: string;
  authorization_code?: string;
  amount?: number;
  currency?: string;
}

export interface GatewayInfo {
  name: string;
  available: boolean;
  public_key?: string;
  supported_currencies: string[];
  supported_payment_methods: string[];
}

export interface GatewayConfig {
  provider: 'stripe' | 'square' | 'clover' | 'paypal' | 'mock';
  publicKey: string;
  environment: 'sandbox' | 'production';
}

// Abstract interface that all gateway adapters must implement
export interface PaymentGatewayAdapter {
  provider: string;
  isAvailable(): boolean;

  // Initialize the gateway (load SDKs, setup, etc.)
  initialize(config: GatewayConfig): Promise<void>;

  // Tokenize payment data securely
  tokenizePayment(paymentData: PaymentData): Promise<PaymentToken>;

  // Get payment method details for display
  getPaymentMethodInfo(token: PaymentToken): {
    brand: string;
    last4: string;
    displayName: string;
  };

  // Cleanup when component unmounts
  cleanup(): void;
}

// Gateway-specific error types
export class PaymentGatewayError extends Error {
  constructor(
    message: string,
    public code?: string,
    public gateway?: string
  ) {
    super(message);
    this.name = 'PaymentGatewayError';
  }
}

export class TokenizationError extends PaymentGatewayError {
  constructor(message: string, code?: string, gateway?: string) {
    super(message, code, gateway);
    this.name = 'TokenizationError';
  }
}

export class GatewayInitializationError extends PaymentGatewayError {
  constructor(message: string, code?: string, gateway?: string) {
    super(message, code, gateway);
    this.name = 'GatewayInitializationError';
  }
}
